<template>
  <div class="dashboard-container">
    <MyTokens
      :selected="activeTab"
      :loading="isLoadingTokens"
      :options="options"
      :title="$t('myTokensTitle')"
      @set-active-tab="setActiveTab"
    />

    <DashboardTokenAddress v-if="$device.isDesktop" />

    <DashboardStatistic v-if="address" />

    <ClientOnly>
      <DashboardPurchases
        v-if="currentToken === Tokens.pearl && address"
        :title="$t(tokensConfig[currentToken]?.label || '', { count: 1 })"
        :token="currentToken"
        :show-description="true"
        :icon="`/img/tokens/${Tokens[currentToken]}.png`"
        @unlocked="toggleIsUnstakeFinished"
      />

      <DashboardPurchases
        v-if="currentToken === Tokens.pearl && address"
        :title="$t('dashboardBlackPearls', { count: 1 })"
        icon="/img/icons/blackPearl.svg"
        :token="Tokens.blackPearl"
        :show-description="true"
        @unlocked="toggleIsUnstakeFinished"
      />

      <DashboardMiningPurchases
        v-if="activityList && currentToken !== Tokens.pearl && address && currentTokenInfo?.isSellable"
        :title="$t(tokensConfig[currentToken]?.label || '', { count: 0 })"
        :minings-table-data="primaryTokenMiningsTableData"
        :token="currentToken"
        @open-purchase-token="openPurchaseTokens"
      />

      <DashboardMiningPurchases
        v-if="
          activityList &&
          currentToken !== Tokens.pearl &&
          address &&
          currentTokenInfo?.isSellable &&
          secondaryTokenMetadata.name.contract !== Tokens.sand
        "
        :title="$t(secondaryTokenMetadata.label, { count: 0 })"
        :token="secondaryTokenMetadata.name.contract as Tokens"
        :minings-table-data="secondaryTokenMiningsTableData"
        @open-purchase-token="() => openPurchaseTokens(true)"
      />
    </ClientOnly>

    <DashboardCharts />

    <el-dialog
      v-model="isPurchaseTokenOpen"
      class="crafting buyPearlModal"
      :class="['without-header', { 'ru-locale': $i18n.locale === 'ru' }]"
      :fullscreen="$device.isMobile"
      :title="selectedTokenTitle"
      :append-to-body="true"
      :center="true"
      @close="store.openedBuilding = null"
    >
      <BuyPearlModal v-if="selectedToken" :token="selectedToken" :is-secondary="isSelectedTokenSecondary" />
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { Tokens, apiUrls, tokensConfig } from '~/utils/constants';
import { useMainStore } from '~/stores/mainStore';
import type { ChartDataModel, ITokenModel, TActivitiesResponse } from '~/types/apiService';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { formattedLastMonthDate, formattedTodayDate } from '~/utils';
import { BigNumber } from 'bignumber.js';
import { formatEther } from 'ethers';
import { type ITokenOptionItem } from '~/types/marketplace';
import tokens from 'public/img/tokens';
import { Formatters } from '~/utils/formatters/formatters';

const { t } = useI18n();
useCookie<string[]>('unstaked', { default: () => [] });
const { blockchain, apiUrl } = useEnvs();
const store = useMainStore();
const { address } = useWeb3ModalAccount();
const { minedBalance, getMinedBalance, isLoading: isLoadingMinedBalance } = useMinedBalance();
const { totalMinedAmount, getTotalMinedAmount, isLoading: isLoadingTotalMinedAmount } = useTotalMined();
const route = useRoute();

const {
  updateData,
  isLoading: isLoadingUpdateDataDashboard,
  totalClaimed,
  saleAmount,
  soldAmount,
  totalStaked,
  maxTotalSupply
} = useDashboard();

const {
  updateData: updateDataStatistics,
  isLoading: isLoadingUpdateDataStatistics,
  blackPearlStakedBalance,
  blackPearlBalance,
  tokenStakedBalance,
  stakedPercent,
  tokenBalance,
  earnedBalance,
  aprPercent,
  currentTokenInfoData
} = useDashboardStatistic();

useHeadSafe({
  title: t('dashboardHeadTitle')
});

const [isUnstakeFinished, toggleIsUnstakeFinished] = useToggle();

const isPurchaseTokenOpen = ref(false);
const selectedToken = ref<Tokens | null>(null);
const isSelectedTokenSecondary = ref(false);
const isTabActive = ref(true);
let refreshInterval: ReturnType<typeof setInterval> | null = null;

const activeTab = ref<Tokens>(store.currentToken || Tokens.pearl);

const setActiveTab = (tab: Tokens) => {
  activeTab.value = tab;
  store.setCurrentToken(tab);
};

const primaryTokenContractAddress = computed(() => blockchain.contracts?.[store.currentToken]?.addresses.contract);
const currentToken = computed(() => store.currentToken);
const unstaked = computed(() => soldAmount.value - totalStaked.value);

const secondaryTokenMetadata = computed(() => {
  return tokenMetaData(
    blockchain.contracts,
    blockchain.contracts[currentTokenInfoData?.value?.key as Tokens]?.addresses.secondaryToken as string
  );
});

const tokenData = computed(() => {
  return tokensConfig[store.currentToken] || null;
});

const selectedTokenTitle = computed(() => {
  if (!selectedToken.value) return '';

  if (selectedToken.value === Tokens.pearl && !isSelectedTokenSecondary.value) {
    return t('corePearl', { count: 1 });
  }

  if (selectedToken.value === Tokens.pearl && isSelectedTokenSecondary.value) {
    return t('dashboardBlackPearls');
  }

  if (selectedToken.value !== Tokens.pearl && !isSelectedTokenSecondary.value) {
    return t(tokenData.value?.label || '', { count: 0 });
  }

  return t(tokenData.value?.extraReward?.label || '', { count: 0 });
});

const openPurchaseTokens = (isSecondary = false): void => {
  isSelectedTokenSecondary.value = isSecondary;
  selectedToken.value = store.currentToken;
  isPurchaseTokenOpen.value = true;
};

const { data: tokensData, status: tokensDataStatus } = useFetch<ITokenModel[]>(apiUrls.token.tokens, {
  query: {
    logic: 'or',
    sellable: 'true'
  },
  baseURL: apiUrl,
  immediate: true,
  server: false
});

const isLoadingTokens = computed(() => tokensDataStatus.value !== 'success');

const { allAprStatus, findApr } = useTokensApr(tokensData, 'dashboard');

const options = computed(() => {
  if (!tokensData.value?.length || allAprStatus.value !== 'success') return [];
  const filtered = tokensData.value
    ?.filter((token) => token.isSellable)
    .map((item) => {
      return {
        ...item,
        src: tokens[item.key],
        label: Formatters.text.splitCamelCase(tokensConfig[item.key].methodName),
        apr: findApr(item.key)
      } as ITokenOptionItem;
    });

  return filtered;
});

const { data: currentTokenInfo } = useFetch<ITokenModel>(() => `${apiUrls.token.tokens}/${currentToken.value}`, {
  baseURL: apiUrl,
  query: { logic: 'or', sellable: 'true', buybackEnabled: 'true' },
  immediate: true,
  watch: [currentToken],
  key: 'dashboardCurrentTokenInfo',
  server: false
});

const { status: soldChartDataStatus } = await useFetch<ChartDataModel>(apiUrls.v1.statistics.sold, {
  baseURL: apiUrl,
  query: {
    from: formattedLastMonthDate(),
    until: formattedTodayDate(),
    token: primaryTokenContractAddress
  },
  watch: [currentToken],
  immediate: true,
  server: false,
  key: 'statistics/sold'
});

const {
  data: activityList,
  status: _activityListStatus,
  refresh
} = useFetch<TActivitiesResponse>(apiUrls.v2.activity, {
  baseURL: apiUrl,
  query: {
    sender: address,
    token: primaryTokenContractAddress
  },
  pick: ['items'],
  watch: [currentToken, address],
  immediate: !!address.value,
  key: '/v2/activity'
});

const isFirstLoading = ref(true);
const activityListStatus = computed(() => {
  if (isFirstLoading.value) {
    return _activityListStatus.value || 'idle';
  } else return 'success';
});

watch(_activityListStatus, (status) => {
  if (status === 'success' && isFirstLoading.value) {
    isFirstLoading.value = false;
  }
});

watch(
  () => [currentToken.value, address.value],
  () => {
    isFirstLoading.value = true;
  }
);

// При смене адреса обновляем данные (кеш автоматически разделен по адресам)
watch(
  () => address.value,
  async (newAddress, oldAddress) => {
    if (oldAddress && newAddress !== oldAddress && newAddress) {
      // Обновляем данные для текущего токена с новым адресом
      await updateData(store.currentToken);

      // Запускаем предзагрузку остальных токенов для нового адреса
      const { preloadAllTokens } = useTokensDataCache();
      const sellableTokens = tokensData.value?.filter((t) => t.isSellable).map((t) => t.key as Tokens) || [];
      if (sellableTokens.length > 0) {
        preloadAllTokens(store.currentToken, sellableTokens);
      }
    }
  }
);

const REFRESH_INTERVAL_MS = 8000;

const startRefreshing = (): void => {
  if (refreshInterval || route.path !== '/dashboard') {
    return;
  }

  refreshInterval = setInterval(() => {
    if (isTabActive.value && route.path === '/dashboard') {
      refresh();
    }
  }, REFRESH_INTERVAL_MS);
};

const stopRefreshing = (): void => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
};

const handleVisibilityChange = (): void => {
  const isVisible = document.visibilityState === 'visible';
  isTabActive.value = isVisible;

  if (isVisible) {
    startRefreshing();
  } else {
    stopRefreshing();
  }
};

const { tableData: primaryTokenMiningsTableData } = useMiningPurchasesTableData();
const { tableData: secondaryTokenMiningsTableData } = useMiningPurchasesTableData(true);

const ownedPrimaryToken = computed(() => {
  return BigNumber(tokenBalance?.value || 0).toFormat(2);
});

const lockedPrimaryToken = computed(() => {
  return BigNumber(tokenStakedBalance?.value || 0).toFormat(2);
});

const lockedSecondaryToken = computed(() => {
  return BigNumber(blackPearlStakedBalance?.value || 0).toFormat(2);
});

const primaryTokenCanClaim = computed(() => {
  if (store.currentToken === Tokens.pearl) {
    return BigNumber(earnedBalance?.value || 0);
  }

  const primaryTokenCanClaim = formatEther(minedBalance?.value?.[0] || 0);
  const secondaryTokenCanClaim = formatEther(minedBalance?.value?.[1] || 0);

  // Token Sand we should sum primary (sand) and secondary (sand) tokens that can be claimed
  if (store.currentToken === Tokens.sand) {
    return BigNumber(primaryTokenCanClaim).plus(secondaryTokenCanClaim);
  }

  return BigNumber(primaryTokenCanClaim);
});

const secondaryTokenCanClaim = computed(() => {
  if (store.currentToken === Tokens.pearl) {
    return;
  }

  return BigNumber(formatEther(minedBalance?.value?.[1] || 0));
});

const totalMinedPrimaryToken = computed(() => {
  return totalMinedAmount?.primaryToken || 0;
});

const totalMinedSecondaryToken = computed(() => {
  return totalMinedAmount?.secondaryToken || 0;
});

// Primary token total produced (primary token total produced + primary token that can be claimed)
const primaryTokenTotalProduced = computed(() => {
  const primaryTokenTotalProduced = totalMinedPrimaryToken.value;
  const secondaryTokenTotalProduced = totalMinedSecondaryToken.value;
  const totalProducedAmount = BigNumber(primaryTokenTotalProduced).plus(secondaryTokenTotalProduced);

  if (store.currentToken === Tokens.sand) {
    return BigNumber(totalProducedAmount).toFormat(2);
  }

  return BigNumber(primaryTokenTotalProduced).toFormat(2);
});

// Secondary token total produced
const secondaryTokenTotalProduced = computed(() => {
  return BigNumber(totalMinedSecondaryToken.value).toFormat(2);
});

onMounted(async () => {
  await updateData(store.currentToken);

  // Предзагружаем данные всех токенов в фоне
  const { preloadAllTokens } = useTokensDataCache();
  const sellableTokens = tokensData.value?.filter((t) => t.isSellable).map((t) => t.key as Tokens) || [];
  if (sellableTokens.length > 0) {
    preloadAllTokens(store.currentToken, sellableTokens);
  }

  document.addEventListener('visibilitychange', handleVisibilityChange);
  if (isTabActive.value && route.path === '/dashboard') {
    startRefreshing();
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  stopRefreshing();
});

watch(
  () => store.currentToken,
  async (currentTokenValue) => {
    await updateData(currentTokenValue);
  }
);

watch(
  () => store.notificationUpdateVersion,
  () => {
    if (address.value) {
      refresh();
    }
  }
);

//useTokenApr
provide('allAprStatus', allAprStatus);
provide('findApr', findApr);

// useDashboard
provide('isLoadingUpdateDataDashboard', isLoadingUpdateDataDashboard);
provide('totalClaimed', totalClaimed);
provide('saleAmountValue', saleAmount);
provide('soldAmountValue', soldAmount);
provide('totalStaked', totalStaked);
provide('maxTotalSupply', maxTotalSupply);

// useDashboardStatistic
provide('updateDataStatistics', updateDataStatistics);
provide('isLoadingUpdateDataStatistics', isLoadingUpdateDataStatistics);
provide('blackPearlBalance', blackPearlBalance);
provide('stakedPercent', stakedPercent);
provide('tokenBalance', tokenBalance);
provide('earnedBalance', earnedBalance);
provide('aprPercent', aprPercent);
provide('currentTokenInfoData', currentTokenInfoData);

// computed
provide('unstaked', unstaked);
provide('primaryTokenTotalProduced', primaryTokenTotalProduced);
provide('secondaryTokenTotalProduced', secondaryTokenTotalProduced);
provide('primaryTokenCanClaim', primaryTokenCanClaim);
provide('secondaryTokenCanClaim', secondaryTokenCanClaim);
provide('soldChartDataStatus', soldChartDataStatus);
provide('isUnstakeFinished', isUnstakeFinished);
provide('isLoadingMinedBalance', isLoadingMinedBalance);
provide('minedBalance', minedBalance);
provide('getMinedBalance', getMinedBalance);
provide('isLoadingTotalMinedAmount', isLoadingTotalMinedAmount);
provide('getTotalMinedAmount', getTotalMinedAmount);
provide('openPurchaseTokens', openPurchaseTokens);
provide('activityListStatus', activityListStatus);
provide('ownedPrimaryToken', ownedPrimaryToken);
provide('lockedPrimaryToken', lockedPrimaryToken);
provide('lockedSecondaryToken', lockedSecondaryToken);
</script>

<style scoped lang="scss">
.dashboard-container {
  margin-top: 40px;
  position: relative;

  .my-tokens {
    padding: 0 16px;
  }

  @media screen and (max-width: 767px) {
    margin-left: auto;
    margin-right: auto;
    margin-top: 0;
  }
}
</style>
